<template>
  <div
    :data-idea-list-id="ideaList.id"
    class="flex-column idea-list-row"
    :class="{ [`view-${viewType}`]: true, 'select-list-item': selectable }"
  >
    <div class="idea-list-row-wrapper" :class="{ expanded }">
      <div class="idea-list-top-bar d-flex flex-grow-1 mx-0" @mouseenter="showOptions = true">
        <i
          class="caret-right-icon caret flex-shrink-0"
          :class="{ expanded: expanded, 'd-none': hideExpansionToggle }"
          @click.stop="handleExpanderClick"
        ></i>
        <div class="d-flex idea-list-meta flex-grow-1">
          <div class="col-auto pl-0"><i class="idea-list-icon"></i></div>
          <div
            class="col d-flex flex-column justify-content-center idea-list-title-col"
            :class="{ 'inline-edit': editable }"
          >
            <input
              type="text"
              class="flex-grow-1 idea-list-title-input"
              v-model="ideaListName"
              @keyup="lastKey = $event.keyCode"
              @keyup.enter="saveIdeaListTitle"
              placeholder="Enter name"
              v-autofocus="editable"
              @blur="
                () =>
                  lastKey !== KEY_CODES.ENTER && lastKey !== KEY_CODES.ESCAPE && saveIdeaListTitle()
              "
              @focus="editable = true"
              data-prevent-blur="true"
              @click.stop
              v-if="editable"
            />
            <h2 class="d-flex flex-grow-1 idea-list-title-wrapper" @click="handleTitleClick" v-else>
              <span
                class="idea-list-title text-medium-1 wrap-hyphenate"
                :title="ideaList.title"
                :class="{ bold: expanded }"
              >
                {{ ideaList.name }}
              </span>
              <span class="icons-list">
                <span
                  :id="'idea-list-expander-' + ideaList.id"
                  class="expander-icon ml-2"
                  @click.stop="openIdeaListPage"
                  v-if="!hideDetailsPageNavigator"
                  ><i></i
                ></span>
                <action-tooltip
                  :target="'idea-list-expander-' + ideaList.id"
                  text="Open"
                  :actions="['Shift', rightKey]"
                />
              </span>
            </h2>
          </div>
        </div>

        <div class="options-menu">
          <idea-list-options
            ref="idea-listOptions"
            :idea-list="ideaList"
            :hide-open-option="hideDetailsPageNavigator"
            @open="openIdeaListPage"
            @edit="editable = true"
            @deleted="$emit('deleted')"
            v-if="showOptions"
          />
        </div>
      </div>
      <b-collapse
        v-model="expanded"
        :class="'ideas ideas-' + ideaList.id"
        v-if="renderSecondLevelItems"
      >
        <div class="spinner-container d-flex justify-content-center" v-if="!ideas">
          <spinner />
        </div>
        <div ref="ideasList" :key="ideasContainerKey" v-if="ideasCopy && ideasCopy.length > 0">
          <idea-header
            class="mt-2"
            @sort="sortIdeas"
            @reset="resetSort"
            @filter="filterIdeas"
            :filtered-by="filteredBy"
            :sorted-by="sortedBy"
            :sorted-direction="sortedDirection"
          />
          <div ref="ideas">
            <idea-row
              v-for="idea in openIdeas"
              :data-id="idea.id"
              :key="idea.localId || idea.id"
              :orgId="ideaList.organizationId"
              :listId="ideaList.id"
              :idea="idea"
              :draggable="!sortedBy"
              :ideas="ideas"
              view-type="list"
              @update-idea="handleIdeaUpdate"
              @delete-idea="deleteIdea"
            ></idea-row>
          </div>
        </div>
        <div class="add-new-idea" v-if="ideas">
          <b-button variant="link" class="text-secondary add-new-btn" @click="addNewIdea">
            Add Idea
          </b-button>
          <b-button
            v-if="completedIdeas.length > 0"
            variant="link"
            class="text-secondary toggle-completed-btn text-medium"
            @click="showCompleted = !showCompleted"
            >{{ showCompleted ? 'Hide Completed' : 'Show Completed' }}
          </b-button>
        </div>
        <b-collapse v-model="showCompleted" class="note" ref="completedIdeas">
          <idea-row
            class="completed"
            v-for="idea in completedIdeas"
            :data-id="idea.id"
            :key="idea.localId || idea.id"
            :orgId="ideaList.organizationId"
            :listId="ideaList.id"
            :idea="idea"
            :ideas="ideas"
            :draggable="false"
            view-type="list"
            @update-idea="handleIdeaUpdate"
            @delete-idea="deleteIdea"
          ></idea-row>
        </b-collapse>
      </b-collapse>
    </div>
  </div>
</template>
<script>
import ActionTooltip from '@/components/ActionTooltip'
import IdeaHeader from '@/components/ideas/IdeaHeader'
import IdeaListOptions from '@/components/ideas/IdeaListOptions'
import IdeaRow from '@/components/ideas/IdeaRow/IdeaRow'
import Spinner from '@/components/Spinner'
import { DEFAULT_EFFORT, DEFAULT_IMPACT, DEFAULT_REACH } from '@/constants/idea'
import { IDEA_LIST_ROW } from '@/constants/ideaList'
import { KEY_CODES } from '@/constants/keyCodes'
import { DEFAULT_ERROR } from '@/constants/messages'
import { TOAST_TYPE } from '@/constants/toasts'
import EventBus from '@/EventBus'
import ExpandCollapseMixin from '@/mixins/ExpandCollapseMixin'
import IdeaDragAndDropMixin from '@/mixins/IdeaDragAndDropMixin'
import IdeasMixin from '@/mixins/IdeasMixin'
import MediaQueryMixin from '@/mixins/MediaQueryMixin'
import * as keymapService from '@/services/keymapService/keymapService'
import filterItems from '@/utils/filterItems'
import handleClickNavigation from '@/utils/handleClickNavigation'
import multiClickHandler from '@/utils/multiClickHandler'
import { getIdeaListPageRouteConfig } from '@/utils/navigationConfigs'
import prettifyKey from '@/utils/prettifyKey'
import sortIdeas from '@/utils/sortIdeas'
import { BButton, BCollapse } from 'bootstrap-vue'
import * as R from 'ramda'
import { mapActions } from 'vuex'
import expansionState from '../../expansionState'

/**
  This is the component that renders view for Idea List
 @emits
 - open
 - updated
 - deleted
 */
export default {
  name: IDEA_LIST_ROW,
  components: {
    BCollapse,
    Spinner,
    IdeaListOptions,
    ActionTooltip,
    IdeaHeader,
    IdeaRow,
    BButton
  },
  props: {
    listId: [String, Number],
    hideExpansionToggle: Boolean,
    hideDetailsPageNavigator: Boolean,
    showDetailsView: Boolean,
    expand: Boolean,
    canToggleExpansion: {
      type: Boolean,
      default: true
    },
    viewType: {
      type: String,
      default: 'list' // list | details
    },
    selectable: {
      type: Boolean,
      default: true
    }
  },
  mixins: [IdeaDragAndDropMixin, IdeasMixin, ExpandCollapseMixin, MediaQueryMixin],
  data() {
    return {
      expanded:
        this.canToggleExpansion && expansionState.exists('idea-list', this.listId)
          ? !!expansionState.get('idea-list', this.listId)
          : this.expand,
      ideas: null,
      ideasCopy: null,
      KEY_CODES,
      showCompleted: false,
      sortedBy: null,
      sortedDirection: null,
      rightKey: prettifyKey('<right>')[0],
      editable: false,
      ideaListName: '',
      lastKey: null,
      showOptions: false,
      // render items that are not supposed to be shown upfront. i.e BCollapse
      renderSecondLevelItems: false,
      filteredBy: {}
    }
  },
  computed: {
    team() {
      return (
        this.ideaList &&
        this.ideaList.type === 'team' &&
        this.$store.getters.teamById(this.ideaList.teamId)
      )
    },
    org() {
      return this.$store.getters.orgById(this.ideaList.organizationId)
    },
    ideaList() {
      return this.$store.getters['idea/ideaListById'](this.listId) || {}
    }
  },
  methods: {
    ...mapActions('idea', ['getIdeaList', 'patchIdeaList']),
    handleExpanderClick() {
      this.toggleExpansionAndSelect()
    },
    handleTitleClick() {
      multiClickHandler({
        onSingleClick: () => {
          const selectedText = window.getSelection().toString()
          if (!selectedText) {
            this.isDesktopMode ? this.toggleExpansionAndSelect() : this.$emit('open')
          }
        },
        onDoubleClick: () => {
          this.editable = true
          this.ideaListName = this.ideaList.name
        }
      })
    },
    toggleExpansionAndSelect() {
      this.expanded = !this.expanded
      this.$nextTick(function() {
        if (this.isCurrentIdeaListSelectable()) {
          this.clearSelection()
          this.selectCurrentIdeaList()
        }
      })
    },
    /*
    This is used by IdeaDragAndDropMixin
     */
    updateIdeas(ideas) {
      this.resetIdeas(ideas)
    },
    addNewIdea() {
      let newIdea = {
        id: this.createIdeaId(),
        title: '',
        impact: DEFAULT_IMPACT,
        effort: DEFAULT_EFFORT,
        reach: DEFAULT_REACH,
        coOwnerIds: [],
        ownerId: null,
        isDone: false
      }
      this.resetIdeas([...(this.ideas || []), newIdea])
      if (this.ideas.length === 1) {
        this.$nextTick(this.enableIdeaDragAndDrop)
      }
    },
    addIdea() {
      if (!this.expanded) {
        this.expanded = true
      }
      setTimeout(this.addNewIdea, 200)
    },
    clearSelection() {
      const selectedItem = keymapService.selectedListItem(document)
      if (selectedItem) selectedItem.classList.remove('selected')
    },
    isCurrentIdeaListSelectable() {
      return this.$el.classList.contains('select-list-item')
    },
    selectCurrentIdeaList() {
      this.$el.classList.add('selected')
    },
    async refreshIdeas() {
      await this.fetchIdeas({ orgId: this.ideaList.organizationId, ideaListId: this.ideaList.id })
    },
    resetIdeas(ideas) {
      this.ideas = ideas
      this.ideasCopy = ideas
    },
    sortIdeas({ by, direction }) {
      this.ideas = sortIdeas.call(this, this.ideas, { by, direction })
      this.sortedBy = by
      this.sortedDirection = direction
    },
    filterIdeas({ filterParam, filterValue, selected }) {
      let appliedFilters = this.filteredBy[filterParam] || {}
      appliedFilters = { ...appliedFilters, [filterValue]: selected }
      this.filteredBy = { ...this.filteredBy, [filterParam]: appliedFilters }
      this.ideas = filterItems({
        filteredBy: this.filteredBy,
        items: this.ideasCopy
      })
    },
    resetSort() {
      this.ideas = this.ideasCopy
      this.sortedBy = null
      this.sortedDirection = null
    },
    cancelInlineEditing() {
      this.editable = false
      this.ideaListName = this.ideaList.name
    },
    async saveIdeaListTitle() {
      try {
        const response = await this.patchIdeaList({
          orgId: this.ideaList.organizationId,
          id: this.ideaList.id,
          data: {
            name: this.ideaListName
          }
        })
        this.editable = false
        this.$emit('updated', response)
      } catch (e) {
        let message =
          R.path(['response', 'data', 'detail', 'name', '0'], e) ||
          R.pathOr(DEFAULT_ERROR, ['response', 'data', 'message'], e)
        EventBus.$emit('ADD_TOAST', { message, type: TOAST_TYPE.ERROR })
      }
    },
    userById(id) {
      return this.$store.getters.userById(id) || {}
    },
    openIdeaListPage(event) {
      const location = getIdeaListPageRouteConfig({
        orgId: this.ideaList.organizationId,
        listId: this.ideaList.id
      })
      handleClickNavigation.call(this, event, location)
    }
  },
  async mounted() {
    if (this.expanded && !this.ideas) {
      this.refreshIdeas().then(() => {
        this.$nextTick(function() {
          setTimeout(() => {
            // adding setTimeout since this.$refs.completedIdeas in not available immediately
            this.enableIdeaDragAndDrop()
            this.updateExpansion(false, '.idea-row', this.$refs.completedIdeas.$el)
          }, 1000)
        })
      })
    }
    setTimeout(() => (this.renderSecondLevelItems = true), 500)
  },
  watch: {
    async expanded(isExpanded) {
      expansionState.set('idea-list', this.ideaList.id, isExpanded)
      if (isExpanded) {
        if (!this.ideas) {
          await this.refreshIdeas()
        }
        this.$nextTick(function() {
          this.enableIdeaDragAndDrop()
        })
      }
      this.$nextTick(function() {
        this.updateExpansion(isExpanded, '.idea-row', this.$refs.ideas)
        this.updateExpansion(
          isExpanded && this.showCompleted,
          '.idea-row',
          this.$refs.completedIdeas.$el
        )
      })
    },
    showCompleted(showCompleted) {
      this.updateExpansion(showCompleted, '.idea-row', this.$refs.completedIdeas.$el)
    },
    editable() {
      this.$nextTick(function() {
        this.clearSelection()
        this.selectCurrentIdeaList()
      })
      this.ideaListName = this.ideaList.name
      this.lastKey = null
    }
  }
}
</script>
<style lang="scss" scoped>
@import '~@/styles/styleConfigurations';
@import '~@/styles/drag';
@import '~@/styles/components/caret';
@import '~@/styles/components/input';

$grid-gutter-width: 6px;
$corner-radius: 10px;

.row {
  margin-left: -$grid-gutter-width;
  margin-right: -$grid-gutter-width;
}

.col,
.col-auto {
  padding-left: $grid-gutter-width;
  padding-right: $grid-gutter-width;
}

.idea-list-row:first-of-type {
  margin-top: 3px;
}

.caret-right-icon {
  margin-top: 6px;
  margin-right: 6px;
}

.idea-list-icon {
  margin-top: 10px;
}

.idea-list-title-wrapper {
  padding: 8px 0 4px 0;
}

.idea-list-row {
  margin-bottom: 5px;
  font-size: 0.875rem;

  .idea-list-row-wrapper.expanded,
  .idea-list-top-bar:hover {
    .gripper {
      display: initial;
    }

    .caret {
      opacity: 1;
    }
  }

  .idea-list-top-bar {
    position: relative;
    padding-left: 16px;
    padding-top: 8px;
    padding-bottom: 8px;
  }

  .idea-list-items {
    padding-left: 6px;
    min-width: 0;
  }

  .idea-list-items.detailed-view {
    padding-top: 1px;
    padding-bottom: 5px;
  }

  .detailed-view .gripper {
    top: 5px;
  }

  &.selected {
    .idea-list-top-bar {
      border-radius: $corner-radius;
      background-color: $selectionColor;
      box-shadow: 0 1px 1px rgba(168, 168, 168, 0.25);
    }

    .caret {
      opacity: 1;
    }
  }

  .ideas {
    padding-bottom: 5px;
  }
}

.idea-list-row:last-of-type {
  margin-bottom: 0px;
}

.idea-list-title-col {
  cursor: pointer;
  line-height: 1.4;
  min-width: 0;
  padding-left: 3px;
}

.idea-list-title-col.inline-edit {
  padding-top: 0;
  padding-bottom: 0;
  padding-left: 0;
}

.inline-edit input {
  padding: 8px 3px;
}

.idea-list-title {
  font-size: 0.9375rem;
  -webkit-line-clamp: 2;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  overflow: hidden;
  height: 100%;
}

.gripper {
  display: none;
  position: absolute;
  left: 6px;
  top: 3px;
  cursor: move;
}

.spinner-container {
  padding: 10px;
}

.add-new-idea {
  padding-left: 38px;
  font-size: 0.75rem;
  padding-top: 5px;
  padding-bottom: 5px;
}

/deep/ .idea-row:last-child .cell {
  border-bottom: 1px solid #edeff1;
}

/deep/ .idea-row.selected:last-child .cell {
  border-color: transparent;
}

/deep/ .idea-row .summary-wrapper {
  padding-right: 0;
}

.add-new-btn {
  font-size: 0.875rem;
}

.add-new-btn:not(:last-child) {
  position: relative;

  &:after {
    content: ' ';
    position: absolute;
    right: -2px;
    top: 13px;
    width: 1px;
    height: 10px;
    background-color: $borderPrimary;
  }
}

.icons-list {
  display: flex;
  align-items: flex-start;
  flex-wrap: nowrap;
  margin-top: 1px;
  margin-left: 8px;
}

.expander-icon,
.edit-icon {
  opacity: 0;
  transition: opacity 200ms ease-out;
}

.idea-list-title-col:hover {
  .expander-icon,
  .edit-icon,
  .comment-badge-icon {
    opacity: 1;
  }
}

/deep/ .ideas-head .row-wrapper .row {
  padding-right: 0;
}

.idea-list-top-bar /deep/ .edit-menu-button[aria-expanded='false'] {
  display: none;
}

.idea-list-top-bar:hover /deep/ .edit-menu-button {
  display: block;
}

.comment-count {
  margin-left: 2px;
}

.comment-badge-icon[data-comment-count='0'] {
  opacity: 0;
}

.idea-list-meta {
  align-self: normal;
}

.idea-list-meta {
  min-width: 0;
}

.options-menu {
  width: 36px;
  flex-shrink: 0;
}

.view-detail {
  .add-new-idea {
    padding-left: 38px;
  }
}

.view-list {
  .idea-list-row-wrapper.expanded {
    margin-bottom: 20px;
  }

  .idea-list-row-wrapper.expanded {
    border: 1px solid $borderPrimary;
    border-radius: 10px;
    .idea-list-top-bar {
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
    }
  }
}

@include media-breakpoint-down(md) {
  .idea-list-row.selected .idea-list-items {
    background-color: inherit;
    box-shadow: none;
  }
  .idea-list-meta {
    .col {
      flex-basis: auto;
    }
  }
  .idea-list-row {
    margin-bottom: 8px;
  }
  .icons-list,
  .options-menu {
    display: none;
  }
}
</style>

<style lang="scss">
@import '~@/styles/styleConfigurations';

.idea-list-row.gu-mirror .idea-row .gripper {
  display: none !important;
}
</style>
